import { Group, MeshBasicMaterial, Color, ShapeGeometry, Mesh, DoubleSide } from 'three';
import Experience from '../Experience.js';
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js';
import { mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export default class SVG {
    constructor(name, url, position, scale) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.camera = this.experience.camera;
        this.raycaster = this.experience.raycaster;
        this.resources = this.experience.resources;
        this.model = null;  // Initialize the model property
        this.addSVG(name, url, position, scale);
    }

    addSVG(name, url, position, scale) {
        const paths = this.resources.items[name].paths;
        const group = new Group();
        group.scale.multiplyScalar(0.25);
        group.position.x = -70;
        group.position.y = 70;
        group.scale.set(scale, scale, scale);
        group.scale.y *= -1;

        // An SVG path becomes one geometry per shape, and these files run to
        // ~90 paths, so a mesh each meant hundreds of draw calls for what is
        // usually a single flat colour. Bucket the geometry by the style it
        // will be drawn with, then emit one merged mesh per bucket. Fills and
        // strokes are kept in separate buckets because ShapeGeometry is
        // indexed and pointsToStroke's output is not - mergeBufferGeometries
        // needs each bucket to agree.
        const buckets = new Map();

        const bucket = (kind, color, opacity, geometry) => {
            if (!geometry) return;
            const key = kind + '|' + color + '|' + (opacity === undefined ? 1 : opacity);
            let entry = buckets.get(key);
            if (!entry) {
                entry = { color, opacity, geometries: [] };
                buckets.set(key, entry);
            }
            entry.geometries.push(geometry);
        };

        for (let i = 0; i < paths.length; i++) {
            const path = paths[i];
            const fillColor = path.userData.style.fill;

            if (fillColor !== undefined && fillColor !== 'none') {
                const shapes = SVGLoader.createShapes(path);

                for (let j = 0; j < shapes.length; j++) {
                    bucket('fill', fillColor, path.userData.style.fillOpacity, new ShapeGeometry(shapes[j]));
                }
            }

            const strokeColor = path.userData.style.stroke;

            if (strokeColor !== undefined && strokeColor !== 'none') {
                for (let j = 0, jl = path.subPaths.length; j < jl; j++) {
                    const subPath = path.subPaths[j];
                    bucket(
                        'stroke',
                        strokeColor,
                        path.userData.style.strokeOpacity,
                        SVGLoader.pointsToStroke(subPath.getPoints(), path.userData.style)
                    );
                }
            }
        }

        for (const entry of buckets.values()) {
            const material = new MeshBasicMaterial({
                color: new Color().setStyle(entry.color).convertSRGBToLinear(),
                opacity: entry.opacity,
                transparent: true,
                side: DoubleSide,
                depthWrite: false,
            });

            // mergeBufferGeometries returns null if the bucket somehow
            // disagrees on attributes; fall back to one mesh each rather than
            // dropping the artwork.
            const merged = entry.geometries.length > 1
                ? mergeBufferGeometries(entry.geometries)
                : entry.geometries[0];

            if (merged) {
                group.add(new Mesh(merged, material));
            } else {
                entry.geometries.forEach(g => group.add(new Mesh(g, material)));
            }
        }
        group.name = name;
        this.model = group;
        group.userData.url = url;
        this.raycaster.objectsToTest.push(group);
        group.position.set(position.x, position.y, position.z);
        group.lookAt(this.camera.instance.position);
        this.scene.add(group);

        // Debug
        this.debug = this.experience.debug;
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder(name + ' SVG');
            this.debugFolder.add(group.position, 'x').name('x').min(-125).max(125).step(0.1);
            this.debugFolder.add(group.position, 'y').name('y').min(-125).max(125).step(0.1);
            this.debugFolder.add(group.position, 'z').name('z').min(-125).max(125).step(0.1);
            this.debugFolder.add(group.rotation, 'x').name('x rotation').min(-125).max(125).step(0.1);
            this.debugFolder.add(group.rotation, 'y').name('y rotation').min(-125).max(125).step(0.1);
            this.debugFolder.add(group.rotation, 'z').name('z rotation').min(-125).max(125).step(0.1);
        }
    }
}
